/** @jsx jsx */
import { useEffect } from 'react';
import { jsx } from '@emotion/core';
import { Typography, useMediaQuery } from '@material-ui/core';
import { XMasonry, XBlock } from 'react-xmasonry';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { categoryContents } from 'actions/content.actions';
import { ICustomTheme } from 'interfaces';
import { contentCategorySelector } from 'reducers/content.reducer';
import { getRandomIntInclusive } from 'core/utils';
import ContentCard from 'components/ContentCard';
import { LOADING_STATUS, STATUS } from 'constants/status';
import {
  EventStatus,
  getPublicationStatusFromDate,
} from 'core/utils/date.helpers_ts';
import { StyledContainer } from './styles';

const SKELETON_LENGTH = 3;
const SKELETON_MIN_HEIGHT = 200;
const SKELETON_MAX_HEIGHT = 450;
const skeletonList = Array.from(Array(SKELETON_LENGTH), (_, index) => ({
  id: index + 1,
  height: getRandomIntInclusive(SKELETON_MIN_HEIGHT, SKELETON_MAX_HEIGHT),
}));

const ContentCategory = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { page, status, contents, totalItems } = useSelector(
    contentCategorySelector,
  );
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  const contentsWithRunningStatus = contents.filter(
    (content) =>
      getPublicationStatusFromDate(content.startDate, content?.endDate) ===
        EventStatus.RUNNING && content.status === STATUS.PUBLISHED,
  );

  useEffect(() => {
    dispatch(categoryContents.request({ id }));
  }, [id, dispatch]);

  const handleGetNextContents = (inView: boolean) => {
    if (inView) {
      dispatch(categoryContents.request({ id, page }));
    }
  };

  if (
    (status === LOADING_STATUS.PENDING || status === LOADING_STATUS.IDLE) &&
    page === 1
  ) {
    return (
      <StyledContainer isMobile={isMobile}>
        <XMasonry maxColumns={2} targetBlockWidth={700}>
          {skeletonList.map((skeleton) => (
            <XBlock key={skeleton.id}>
              <ContentCard skeleton={skeleton} />
            </XBlock>
          ))}
        </XMasonry>
      </StyledContainer>
    );
  }

  if (!contentsWithRunningStatus?.length) {
    return (
      <StyledContainer isMobile={isMobile}>
        <Typography variant="h4">{t('core.no_content')}</Typography>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer isMobile={isMobile}>
      <XMasonry maxColumns={2} targetBlockWidth={700}>
        {contentsWithRunningStatus.map((content, index) => (
          <XBlock key={content.id}>
            {index === contents.length - 1 && contents.length < totalItems ? (
              <InView onChange={handleGetNextContents}>
                <ContentCard content={content} />
              </InView>
            ) : (
              <ContentCard content={content} />
            )}
          </XBlock>
        ))}
        {status === LOADING_STATUS.PENDING && (
          <XBlock key="skeleton">
            <ContentCard skeleton={{ height: 250 }} />
          </XBlock>
        )}
      </XMasonry>
    </StyledContainer>
  );
};

export default ContentCategory;
